import type { AudioType } from './index.types';
import {
  SECOND_INCOMING_CALL_INTERVAL_MS,
  AUDIO_CONFIGS,
} from 'Modules/softphone/constants';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { audioLogger as logger } from 'Modules/softphone/utils/logger';
export type { AudioType } from './index.types';

const RING_AUDIO_TYPES: ReadonlySet<AudioType> = new Set<AudioType>([
  'ringtone',
  'ringback',
  'secondIncoming',
]);

/**
 * Manages HTMLAudioElement instances for softphone sounds (ringtones, ringback, call audio).
 * Handles playback, volume, speaker selection (setSinkId), and remote call streams.
 *
 * Audio playing state is synced to Zustand store for React components.
 */
export class AudioService {
  private static instance: AudioService;
  private audioElements: Map<AudioType, HTMLAudioElement> = new Map();
  private playPromises: Map<AudioType, Promise<void>> = new Map();
  private selectedSpeakerId: string | null = null;
  private selectedRingerId: string | null = null;
  private secondIncomingInterval: ReturnType<typeof setInterval> | null = null;

  private constructor() { }

  static getInstance(): AudioService {
    if (!AudioService.instance) {
      AudioService.instance = new AudioService();
    }
    return AudioService.instance;
  }

  /**
   * Resolve which output device an audio type should use:
   * ring-type sounds follow the ring device, everything else the speaker device.
   */
  private getSinkIdForType(type: AudioType): string | null {
    return RING_AUDIO_TYPES.has(type)
      ? this.selectedRingerId
      : this.selectedSpeakerId;
  }

  /**
   * Apply the appropriate output device (setSinkId) to a single audio element
   * based on its type. No-op when no device is selected yet or setSinkId is
   * unsupported (e.g. Firefox).
   */
  private applySinkToElement(element: HTMLAudioElement, type: AudioType): void {
    const deviceId = this.getSinkIdForType(type);

    if (deviceId && 'setSinkId' in element) {
      (
        element as HTMLAudioElement & {
          setSinkId: (id: string) => Promise<void>;
        }
      )
        .setSinkId(deviceId)
        .then(() => {
          logger.info(`Sink applied to ${type}: ${deviceId}`);
        })
        .catch((err) => {
          logger.warn(`Failed to set sink on ${type}:`, {
            error: err instanceof Error ? err : new Error(String(err)),
          });
        });
    } else {
      logger.debug(
        `No sink applied to ${type} (deviceId: ${deviceId}, supported: ${String(
          'setSinkId' in element
        )})`
      );
    }
  }

  /**
   * True while the element is holding the output device: playing a tone, or
   * carrying a live call stream. Only those follow a device change straight
   * away; an idle element picks the new device up on its next play, so that
   * choosing a speaker never opens an audio session on its own.
   */
  private isUsingOutputDevice(audio: HTMLAudioElement): boolean {
    return !audio.paused || !!audio.srcObject;
  }

  /**
   * Register a pre-created audio element from the DOM
   * This bypasses browser autoplay restrictions by using elements that exist in the DOM
   *
   * IMPORTANT: Call this before playing audio to ensure DOM elements are used
   * instead of dynamically created ones.
   *
   * Deliberately does not bind the output device: registration happens on page
   * load, and setSinkId there opens an audio session on the OS while nothing
   * is playing, which disturbs other applications (CUU2-3411). play() and
   * setRemoteStream() bind it when the sound is about to be heard.
   */
  registerAudioElement(type: AudioType, element: HTMLAudioElement): void {
    // Apply configuration from AUDIO_CONFIGS
    const config = AUDIO_CONFIGS[type];

    if (config.src && !element.src) {
      element.src = config.src;
    }

    element.volume = config.volume;
    element.loop = config.loop;

    if (config.preload) {
      element.preload = 'auto';
    }

    this.audioElements.set(type, element);
    logger.debug(`Registered pre-created audio element for: ${type}`);
  }

  /**
   * Get or create an audio element for the specified type
   * Lazy initialization ensures elements are only created when needed
   */
  private getOrCreateAudio(type: AudioType): HTMLAudioElement {
    let audio = this.audioElements.get(type);

    if (!audio) {
      const config = AUDIO_CONFIGS[type];
      audio = new Audio();

      if (config.src) {
        audio.src = config.src;
      }

      audio.volume = config.volume;
      audio.loop = config.loop;

      if (config.preload) {
        audio.preload = 'auto';
      }

      // Add CSS class for speaker selection
      audio.classList.add('softphone-audio');
      audio.id = `softphone-${type}`;

      this.audioElements.set(type, audio);
      logger.debug(`Created audio element for: ${type}`);
    }

    return audio;
  }

  /**
   * Play an audio type
   * Handles autoplay restrictions gracefully
   * Special handling for secondIncoming: plays once, then repeats every 10 seconds
   */
  async play(type: AudioType): Promise<void> {
    const audio = this.getOrCreateAudio(type);

    // Don't restart if already playing
    if (!audio.paused) {
      logger.debug(`${type} already playing, skipping`);
      return;
    }

    try {
      // Bind the output device now that the sound is about to be heard.
      this.applySinkToElement(audio, type);

      audio.currentTime = 0;
      const playPromise = audio.play();
      this.playPromises.set(type, playPromise);
      await playPromise;
      logger.debug(`${type} started playing`);
      // Sync state to Zustand store
      useSoftphoneStore.getState().setAudioPlaying(type, true);

      // Special handling for secondIncoming: repeat every 10 seconds
      if (type === 'secondIncoming') {
        this.setupSecondIncomingRepeat(audio);
      }
    } catch (error) {
      const errorName = (error as Error)?.name || 'Unknown';

      // Expected errors that we can safely ignore
      if (
        errorName === 'NotAllowedError' ||
        errorName === 'NotSupportedError'
      ) {
        logger.warn(`Expected error playing ${type}: ${errorName}`);
      } else {
        logger.error(`Unexpected error playing ${type}:`, {
          error: error instanceof Error ? error : new Error(String(error))
        });
      }
    }
  }

  /**
   * Setup repeat logic for second incoming call chime
   * Plays immediately, then repeats every 10 seconds using setInterval
   */
  private setupSecondIncomingRepeat(audio: HTMLAudioElement): void {
    // Clear any existing interval
    if (this.secondIncomingInterval) {
      clearInterval(this.secondIncomingInterval);
      this.secondIncomingInterval = null;
      logger.debug('Cleared existing secondIncoming interval');
    }

    // Play immediately
    audio.currentTime = 0;
    audio.play().catch((err) => {
      logger.warn('Failed to play secondIncoming initially:', {
        error: new Error(String(err)),
      });
    });

    // Setup interval to replay every 10 seconds
    this.secondIncomingInterval = setInterval(() => {
      const currentAudio = this.audioElements.get('secondIncoming');
      if (currentAudio) {
        currentAudio.currentTime = 0;
        currentAudio.play().catch((err) => {
          logger.warn('Failed to replay secondIncoming:', {
            error: new Error(String(err)),
          });
        });
      }
    }, SECOND_INCOMING_CALL_INTERVAL_MS);

    logger.debug('Setup secondIncoming repeat interval (10s)');
  }

  /**
   * Stop an audio type
   * Clears any pending repeat intervals for secondIncoming
   */
  async stop(type: AudioType): Promise<void> {
    const audio = this.audioElements.get(type);
    if (!audio) return;

    try {
      // Wait for any pending play promise
      const playPromise = this.playPromises.get(type);
      if (playPromise !== undefined && playPromise !== null) {
        await playPromise;
      }
    } catch {
      // Ignore errors from play promise
    }

    // Clear secondIncoming interval if stopping that audio
    if (type === 'secondIncoming') {
      if (this.secondIncomingInterval) {
        clearInterval(this.secondIncomingInterval);
        this.secondIncomingInterval = null;
        logger.debug('Cleared secondIncoming repeat interval');
      }
    }

    audio.pause();
    audio.currentTime = 0;
    this.playPromises.delete(type);
    logger.debug(`${type} stopped`);
    // Sync state to Zustand store
    useSoftphoneStore.getState().setAudioPlaying(type, false);
  }

  /**
   * Get the raw audio element for remote call audio
   * Used to connect MediaStream to audio output
   */
  getRemoteAudioElement(): HTMLAudioElement {
    return this.getOrCreateAudio('remoteAudio');
  }

  /**
   * Set the MediaStream for remote audio playback
   */
  setRemoteStream(stream: MediaStream | null): void {
    const audio = this.getRemoteAudioElement();

    if (stream) {
      logger.info('Setting remote audio stream', {
        trackCount: stream.getTracks().length,
        tracks: stream.getTracks().map((t) => ({
          kind: t.kind,
          enabled: t.enabled,
          readyState: t.readyState,
          id: t.id,
          muted: t.muted,
        })),
        currentSinkId: this.selectedSpeakerId,
        audioElementId: audio.id,
        audioVolume: audio.volume,
        audioMuted: audio.muted,
        audioAutoplay: audio.autoplay,
      });

      // Ensure audio element is properly configured
      audio.autoplay = true;
      audio.muted = false;
      audio.volume = 1.0;

      audio.srcObject = stream;

      // Ensure speaker is set (in case it wasn't set during creation)
      if (this.selectedSpeakerId && 'setSinkId' in audio) {
        (
          audio as HTMLAudioElement & {
            setSinkId: (id: string) => Promise<void>;
          }
        )
          .setSinkId(this.selectedSpeakerId)
          .then(() => {
            logger.info('Speaker applied to remoteAudio after stream set', {
              speakerId: this.selectedSpeakerId,
            });
          })
          .catch((err) => {
            logger.error('Failed to set speaker on remoteAudio', {
              error: new Error(String(err)),
            });
          });
      } else {
        logger.warn('No speaker selected or setSinkId not supported', {
          hasSpeaker: !!this.selectedSpeakerId,
          hasSetsinkId: 'setSinkId' in audio,
        });
      }

      audio.play().catch((err) => {
        logger.error('Failed to play remote audio', {
          error: new Error(String(err)),
        });
      });
      logger.info('Remote audio stream connected and playing');
    } else {
      audio.srcObject = null;
      audio.pause();
      logger.debug('Remote audio stream disconnected');
    }
  }

  /**
   * Set the speaker device for call/non-ring audio elements.
   * Ring-type sounds (ringtone, ringback, secondIncoming) are intentionally
   * skipped — they follow the ring device (setRinger), so changing the speaker
   * must not move them. Mirrors legacy PhoneStore.configureSpeaker, which only
   * applies the speaker sink to the live call audio.
   */
  async setSpeaker(deviceId: string): Promise<void> {
    this.selectedSpeakerId = deviceId;

    const promises: Promise<void>[] = [];

    this.audioElements.forEach((audio, type) => {
      // Ring-type sounds are controlled by setRinger, not the speaker device.
      if (RING_AUDIO_TYPES.has(type)) {
        return;
      }

      // Idle elements only store the preference: see isUsingOutputDevice.
      if (!this.isUsingOutputDevice(audio)) {
        return;
      }

      if ('setSinkId' in audio) {
        const promise = (
          audio as HTMLAudioElement & {
            setSinkId: (id: string) => Promise<void>;
          }
        )
          .setSinkId(deviceId)
          .then(() => {
            logger.debug(`Speaker set for ${type}: ${deviceId}`);
          })
          .catch((err) => {
            logger.error(`Failed to set speaker for ${type}:`, {
              error: new Error(String(err)),
            });
          });
        promises.push(promise);
      }
    });

    await Promise.all(promises);
  }

  /**
   * Set the ring device for ring-type audio elements only
   * (ringtone, ringback, secondIncoming). Leaves the speaker/call audio
   * untouched. Mirrors legacy PhoneStore.configureRingTone.
   */
  async setRinger(deviceId: string): Promise<void> {
    this.selectedRingerId = deviceId;

    const promises: Promise<void>[] = [];

    this.audioElements.forEach((audio, type) => {
      // Only ring-type sounds follow the ring device.
      if (!RING_AUDIO_TYPES.has(type)) {
        return;
      }

      if (!this.isUsingOutputDevice(audio)) {
        return;
      }

      if ('setSinkId' in audio) {
        const promise = (
          audio as HTMLAudioElement & {
            setSinkId: (id: string) => Promise<void>;
          }
        )
          .setSinkId(deviceId)
          .then(() => {
            logger.debug(`Ringer set for ${type}: ${deviceId}`);
          })
          .catch((err) => {
            logger.error(`Failed to set ringer for ${type}:`, {
              error: new Error(String(err)),
            });
          });
        promises.push(promise);
      }
    });

    await Promise.all(promises);
  }

  /**
   * Check if an audio type is currently playing
   */
  isPlaying(type: AudioType): boolean {
    const audio = this.audioElements.get(type);
    return audio ? !audio.paused : false;
  }

  /**
   * Set volume for a specific audio type
   */
  setVolume(type: AudioType, volume: number): void {
    const audio = this.audioElements.get(type);
    if (audio) {
      audio.volume = Math.max(0, Math.min(1, volume));
      logger.debug(`Volume set for ${type}: ${volume}`);
    }
  }

  /**
   * Get current volume for a specific audio type
   */
  getVolume(type: AudioType): number {
    const audio = this.audioElements.get(type);
    return audio ? audio.volume : 0;
  }

  /**
   * Cleanup all audio elements
   * Should be called on app unmount
   *
   * IMPORTANT: Preserves remoteAudio when there are active calls to ensure
   * call audio continues working during Provider remounts.
   */
  cleanup(): void {
    // Check if there are active calls - if so, preserve remoteAudio
    const hasActiveCalls = useSoftphoneStore.getState().activeCalls.size > 0;

    if (hasActiveCalls) {
      logger.debug('Active calls detected, preserving remoteAudio');
    }

    // Clear secondIncoming interval
    if (this.secondIncomingInterval) {
      clearInterval(this.secondIncomingInterval);
      this.secondIncomingInterval = null;
      logger.debug('Cleared secondIncoming interval during cleanup');
    }

    const typesToRemove: AudioType[] = [];

    this.audioElements.forEach((audio, type) => {
      // Preserve remoteAudio if there are active calls
      if (type === 'remoteAudio' && hasActiveCalls) {
        logger.debug('Preserving remoteAudio for active call');
        return;
      }

      audio.pause();
      audio.src = '';
      audio.srcObject = null;
      // Reset store state
      useSoftphoneStore.getState().setAudioPlaying(type, false);
      typesToRemove.push(type);
      logger.debug(`Cleaned up ${type}`);
    });

    // Remove cleaned up elements
    typesToRemove.forEach((type) => {
      this.audioElements.delete(type);
      this.playPromises.delete(type);
    });

    logger.debug('AudioService cleanup complete');
  }

  /**
   * Cleanup specific audio type
   */
  cleanupAudio(type: AudioType): void {
    const audio = this.audioElements.get(type);
    if (audio) {
      // Clear secondIncoming interval if cleaning up that audio
      if (type === 'secondIncoming') {
        if (this.secondIncomingInterval) {
          clearInterval(this.secondIncomingInterval);
          this.secondIncomingInterval = null;
          logger.debug('Cleared secondIncoming interval during cleanupAudio');
        }
      }

      audio.pause();
      audio.src = '';
      audio.srcObject = null;
      this.audioElements.delete(type);
      this.playPromises.delete(type);
      // Reset store state
      useSoftphoneStore.getState().setAudioPlaying(type, false);
      logger.debug(`Cleaned up ${type}`);
    }
  }

  /**
   * Stop all inbound audio (ringtone and secondIncoming)
   */
  stopAllInbound(): void {
    void this.stop('ringtone');
    void this.stop('secondIncoming');
    logger.debug('Stopped all inbound audio');
  }

  /**
   * Stop all call-related audio (ringback, ringtone, secondIncoming)
   */
  stopAllCallAudio(): void {
    void this.stop('ringback');
    void this.stop('ringtone');
    void this.stop('secondIncoming');
    logger.debug('Stopped all call audio');
  }

  /**
   * Play lost connection tone
   * Used when network connection is lost during an active call
   */
  playLostConnectionTone(): void {
    void this.play('lostConnection');
    logger.info('Playing lost connection tone');
  }

  /**
   * Stop lost connection tone
   * Used when network connection is restored
   */
  stopLostConnectionTone(): void {
    void this.stop('lostConnection');
    logger.info('Stopped lost connection tone');
  }

  /**
   * Play DTMF tone for a digit
   * Matches legacy behavior with 0.2 volume
   * Forces restart even if already playing to avoid audio artifacts
   */
  playDTMFTone(digit: string): void {
    const dtmfMap: Record<string, AudioType> = {
      '0': 'dtmf0',
      '1': 'dtmf1',
      '2': 'dtmf2',
      '3': 'dtmf3',
      '4': 'dtmf4',
      '5': 'dtmf5',
      '6': 'dtmf6',
      '7': 'dtmf7',
      '8': 'dtmf8',
      '9': 'dtmf9',
      '*': 'dtmfStar',
      '#': 'dtmfPound',
    };

    const audioType = dtmfMap[digit];
    if (!audioType) {
      logger.warn(`Invalid DTMF digit: ${digit}`);
      return;
    }

    const audio = this.getOrCreateAudio(audioType);

    // Force stop and reset to avoid audio artifacts at the end
    audio.pause();
    audio.currentTime = 0;

    // Sync state to Zustand store for DevTools visibility
    useSoftphoneStore.getState().setAudioPlaying(audioType, true);

    // Play the tone
    audio.play().catch((error) => {
      logger.warn(`Failed to play DTMF tone for ${digit}:`, {
        error: error instanceof Error ? error : new Error(String(error)),
      });
    });

    // Clear playing state when tone ends (DTMF tones are short)
    audio.onended = () => {
      useSoftphoneStore.getState().setAudioPlaying(audioType, false);
    };

    logger.debug(`Playing DTMF tone for digit: ${digit}`);
  }
}
